import Phaser from 'phaser';
import { STORY_EVENTS, type StoryEvent, type StoryReward } from '../data/story';
import type { GameScene } from '../scenes/GameScene';

export class StorySystem {
  private scene: GameScene;
  private completedEvents: Set<string> = new Set();
  private harvestCount: number = 0;
  private battleWins: number = 0;
  private farmExpansions: number = 0;
  private nightsSurvived: number = 0;
  private isShowingDialogue: boolean = false;
  private pendingChain?: string;

  // Dialogue UI elements
  private dialogueContainer?: Phaser.GameObjects.Container;

  constructor(scene: GameScene) {
    this.scene = scene;
    this.setupListeners();
  }

  private setupListeners(): void {
    // Track events for triggers
    this.scene.events.on('plant-harvested', () => {
      this.harvestCount++;
      this.checkTriggers();
    });

    this.scene.events.on('battle-result', (data: { won: boolean }) => {
      if (data.won) {
        this.battleWins++;
        this.checkTriggers();
      }
    });

    this.scene.events.on('farm-expanded', () => {
      this.farmExpansions++;
      this.checkTriggers();
    });

    this.scene.events.on('night-survived', (count: number) => {
      this.nightsSurvived = count;
      this.checkTriggers();
    });

    this.scene.events.on('new-day', () => {
      this.checkTriggers();
    });
  }

  /** Called once at game start to fire the intro event. */
  startStory(): void {
    const intro = STORY_EVENTS.find(e => e.id === 'intro');
    if (intro) {
      this.scene.time.delayedCall(1500, () => {
        this.triggerEvent(intro);
      });
    }
  }

  checkTriggers(): void {
    if (this.isShowingDialogue) return;

    // Check pending chain first
    if (this.pendingChain) {
      const chainEvent = STORY_EVENTS.find(e => e.id === this.pendingChain);
      if (chainEvent && !this.completedEvents.has(chainEvent.id)) {
        this.pendingChain = undefined;
        this.scene.time.delayedCall(500, () => this.triggerEvent(chainEvent));
        return;
      }
      this.pendingChain = undefined;
    }

    const dayCount = this.scene.dayNightSystem.getDayCount();

    for (const event of STORY_EVENTS) {
      if (this.completedEvents.has(event.id)) continue;

      const t = event.trigger;
      let shouldFire = false;

      switch (t.type) {
        case 'day':
          shouldFire = dayCount >= (t.value ?? 0);
          break;
        case 'harvest':
          shouldFire = this.harvestCount >= (t.value ?? 0);
          break;
        case 'battle_win':
          shouldFire = this.battleWins >= (t.value ?? 0);
          break;
        case 'farm_expand':
          shouldFire = this.farmExpansions > 0;
          break;
        case 'night_survive':
          shouldFire = this.nightsSurvived >= (t.value ?? 0);
          break;
        case 'plant_unlock':
          shouldFire = this.scene.player.unlockedPlants.includes(t.plantId ?? '');
          break;
      }

      if (shouldFire) {
        this.scene.time.delayedCall(500, () => this.triggerEvent(event));
        return; // Only trigger one at a time
      }
    }
  }

  private triggerEvent(event: StoryEvent): void {
    if (this.completedEvents.has(event.id)) return;
    this.completedEvents.add(event.id);
    this.isShowingDialogue = true;

    // Apply rewards immediately (seeds, coins, unlocks)
    if (event.reward) {
      this.applyReward(event.reward);
    }

    if (event.nextEvent) {
      this.pendingChain = event.nextEvent;
    }

    // Show dialogue in UI scene
    this.showDialogue(event);
  }

  private applyReward(reward: StoryReward): void {
    if (reward.seeds) {
      for (const seed of reward.seeds) {
        this.scene.player.addToInventory(seed.plantId, seed.quantity);
      }
    }
    if (reward.coins && this.scene.combatSystem) {
      this.scene.combatSystem.playerCoins += reward.coins;
    }
    if (reward.unlockPlant) {
      this.scene.player.unlockPlant(reward.unlockPlant);
    }
  }

  private showDialogue(event: StoryEvent): void {
    // Get UIScene to render dialogue
    const uiScene = this.scene.scene.get('UIScene') as Phaser.Scene;
    if (!uiScene) {
      this.isShowingDialogue = false;
      return;
    }

    const width = uiScene.cameras.main.width;
    const height = uiScene.cameras.main.height;

    this.dialogueContainer = uiScene.add.container(width / 2, height - 100);
    this.dialogueContainer.setDepth(500);

    const panelW = 700;
    const panelH = 130;

    // Dark panel background
    const bg = uiScene.add.graphics();
    bg.fillStyle(0x0a0a1e, 0.92);
    bg.fillRoundedRect(-panelW / 2, -panelH / 2, panelW, panelH, 8);
    bg.lineStyle(2, 0x6aaa3a);
    bg.strokeRoundedRect(-panelW / 2, -panelH / 2, panelW, panelH, 8);
    this.dialogueContainer.add(bg);

    // Title
    const titleColor = event.speaker === 'Thornwick' ? '#d4573b' : '#6aaa3a';
    const titleText = uiScene.add.text(-panelW / 2 + 15, -panelH / 2 + 8, `${event.speaker ?? 'Narrator'} — ${event.title}`, {
      fontFamily: 'monospace',
      fontSize: '12px',
      color: titleColor,
      fontStyle: 'bold',
    });
    this.dialogueContainer.add(titleText);

    // Show lines one at a time
    let lineIndex = 0;
    const lineText = uiScene.add.text(-panelW / 2 + 15, -panelH / 2 + 28, event.text[0], {
      fontFamily: 'monospace',
      fontSize: '11px',
      color: '#dddddd',
      wordWrap: { width: panelW - 30 },
      lineSpacing: 4,
    });
    this.dialogueContainer.add(lineText);

    // "Click to continue" hint
    const hintText = uiScene.add.text(panelW / 2 - 15, panelH / 2 - 12, 'Click to continue...', {
      fontFamily: 'monospace',
      fontSize: '9px',
      color: '#888888',
    });
    hintText.setOrigin(1, 1);
    this.dialogueContainer.add(hintText);

    // Advance on click or key
    const advance = () => {
      lineIndex++;
      if (lineIndex < event.text.length) {
        lineText.setText(event.text[lineIndex]);
      } else {
        // Done — clean up
        this.dialogueContainer?.destroy();
        this.dialogueContainer = undefined;
        this.isShowingDialogue = false;
        uiScene.input.off('pointerdown', advance);
        // Check for chained events
        this.scene.time.delayedCall(300, () => this.checkTriggers());
      }
    };

    uiScene.input.on('pointerdown', advance);

    // Also advance on Space/Enter/E key
    const keyAdvance = (e: KeyboardEvent) => {
      if (e.code === 'Space' || e.code === 'Enter' || e.code === 'KeyE') {
        e.preventDefault();
        advance();
        if (lineIndex >= event.text.length) {
          window.removeEventListener('keydown', keyAdvance);
        }
      }
    };
    window.addEventListener('keydown', keyAdvance);
  }
}
